import {
  useMutation,
  useQuery,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';

import { apiClient, fleetPath } from '@/lib/api-client';
import type { PreflightResult, PreflightStatus } from '@/types/provision';

const TERMINAL: ReadonlySet<PreflightStatus> = new Set(['success', 'failed']);

/** POST /servers/:name/preflight — dispatch the preflight playbook. */
export function useDispatchPreflight(): UseMutationResult<{ task_id: number }, Error, string> {
  return useMutation({
    mutationFn: (serverName) =>
      apiClient.post<{ task_id: number }>(
        fleetPath(`/servers/${encodeURIComponent(serverName)}/preflight`),
        undefined,
      ),
  });
}

/**
 * GET /servers/:name/preflight — polls every 2s until the run reaches a
 * terminal state. `enabled` lets the wizard hold off until a run is dispatched.
 */
export function usePreflightResult(
  serverName: string | undefined,
  enabled: boolean,
): UseQueryResult<PreflightResult, Error> {
  return useQuery({
    queryKey: ['fleet', 'preflight', serverName ?? ''],
    enabled: Boolean(serverName) && enabled,
    queryFn: ({ signal }) =>
      apiClient.get<PreflightResult>(
        fleetPath(`/servers/${encodeURIComponent(serverName as string)}/preflight`),
        signal,
      ),
    refetchInterval: (q) => {
      const status = q.state.data?.status;
      return status && TERMINAL.has(status) ? false : 2_000;
    },
  });
}
